import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { ArtifactTree } from '../types/artifacts';
import { checkRootStatus } from '../store/slices/ArtifactSlice';
import { useAppDispatch } from '../store/hooks';
import { useUser } from './UserContext';

interface ArtifactTreeContextType {
  selectedTree: ArtifactTree | null;
  setSelectedTree: (tree: ArtifactTree | null) => void;
  reloadTree: () => void;
  isTreeLoading: boolean;
  treeError: string | null;
}

const ArtifactTreeContext = createContext<ArtifactTreeContextType | undefined>(undefined);

const useArtifactTreeState = () => {
  const dispatch = useAppDispatch();
  const { selectedCompany } = useUser();
  const [selectedTree, setSelectedTree] = useState<ArtifactTree | null>(null);
  const [isTreeLoading, setIsTreeLoading] = useState(false);
  const [treeError, setTreeError] = useState<string | null>(null);

  const reloadTree = useCallback(async () => {
    if (!selectedTree) return;

    try {
      setIsTreeLoading(true);
      setTreeError(null);
      await dispatch(checkRootStatus({ treeId: selectedTree.id, companyId: selectedCompany?.id })).unwrap();
    } catch (error) {
      setTreeError(error instanceof Error ? error.message : 'Failed to reload tree');
    } finally {
      setIsTreeLoading(false);
    }
  }, [dispatch, selectedTree, selectedCompany?.id]);

  const handleSetSelectedTree = useCallback((tree: ArtifactTree | null) => {
    setSelectedTree(tree);
    // Don't automatically reload the tree when selecting it
  }, []);

  return {
    selectedTree,
    setSelectedTree: handleSetSelectedTree,
    reloadTree,
    isTreeLoading,
    treeError,
  };
};

export const ArtifactTreeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const treeState = useArtifactTreeState();

  return <ArtifactTreeContext.Provider value={treeState}>{children}</ArtifactTreeContext.Provider>;
};

export const useArtifactTree = () => {
  const context = useContext(ArtifactTreeContext);
  if (!context) {
    throw new Error('useArtifactTree must be used within an ArtifactTreeProvider');
  }
  return context;
};
